import {compareDates} from "./dateUtil";

export const sortCaseInsensitive = (list: string[]): string[] => {
    return list.slice().sort((a, b) => {
        return a.trim().toLowerCase().localeCompare(b.trim().toLowerCase());
    });
};

export function groupItemsByYear<T>(items: T[], getDate: (item: T) => Date | null): Map<number, T[]> {
    const map = new Map<number, T[]>();

    items
        .sort((item1, item2) => compareDates(getDate(item1), getDate(item2)))
        .forEach((item: T) => {
            const year = getDate(item) ? getDate(item)!.getFullYear() : 0;

            if (!map.has(year)) {
                map.set(year, []);
            }

            map.get(year)!.push(item);
        });

    return map;
}

export interface INamed {
    name: string;
}

export interface IHasEmail {
    email: string;
}

export const byName = (item1: INamed, item2: INamed) => {
    return compareInsensitive(item1.name, item2.name);
};

export const byEmail = (item1: IHasEmail, item2: IHasEmail) => {
    return compareInsensitive(item1.email, item2.email);
};

export const byNameReversed = (item1: INamed, item2: INamed) => {
    return compareInsensitive(item2.name, item1.name);
};

export const compareInsensitive = (s1: string | null, s2: string | null) => {
    const a = (s1 ?? '').toLowerCase();
    const b = (s2 ?? '').toLowerCase();
    if (a > b) return 1;
    if (a < b) return -1;
    return 0;
};

export type SortDir = "asc" | "desc";

/**
 * Generic field-based sort for table data. Handles strings (case-insensitive),
 * booleans, and numbers. Returns a new sorted array without mutating the original.
 */
export const applySort = <T extends Record<string, any>>(arr: T[], col: string, dir: SortDir): T[] => {
    if (!col) return arr;
    const mult = dir === "asc" ? 1 : -1;
    return [...arr].sort((a, b) => {
        const av = a[col];
        const bv = b[col];
        if (typeof av === "string" && typeof bv === "string") return mult * av.toLowerCase().localeCompare(bv.toLowerCase());
        if (typeof av === "boolean") return mult * ((av ? 1 : 0) - (bv ? 1 : 0));
        return mult * ((av ?? 0) - (bv ?? 0));
    });
};
